import { useState } from 'react';

import { DateField } from '../../../../../components/inputs/DateField';
import { Select } from '../../../../../components/inputs/Select';
import { TextField } from '../../../../../components/inputs/TextField';
import { todayCalendarDate } from '../../../../../components/calendar/monthGrid';
import {
  MILITARY_BRANCH_LABELS,
  MILITARY_SERVICE_STATUS_LABELS,
  type MilitaryBranch,
  type MilitaryServiceStatus,
  type PersonnelDetail,
} from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';
import { PersonnelSectionModal } from './PersonnelSectionModal';

import { useSectionForm } from './sectionForm';

/**
 * The Military card's editor.
 *
 * `isMilitary` is **not** a field here, and that is deliberate. The flag decides whether this
 * panel exists at all, so turning it off from inside the panel would delete the surface the
 * reader is standing on. It belongs to the Personal section, which is where the create form
 * asks it.
 *
 * The branch is required once the record claims service — everything else is optional, because
 * the details are collected later as often as not.
 */
export function EditMilitaryModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [branch, setBranch] = useState<MilitaryBranch | null>(null);
  const [status, setStatus] = useState<MilitaryServiceStatus | null>(null);
  const [rank, setRank] = useState('');
  const [serviceNumber, setServiceNumber] = useState('');
  const [unit, setUnit] = useState('');
  const [entered, setEntered] = useState('');
  const [separated, setSeparated] = useState('');

  function seed() {
    setBranch(record.militaryBranch);
    setStatus(record.militaryServiceStatus);
    setRank(record.militaryRank ?? '');
    setServiceNumber(record.militaryServiceNumber ?? '');
    setUnit(record.militaryUnit ?? '');
    setEntered(record.militaryDateEnteredService ?? '');
    setSeparated(record.militaryDateSeparated ?? '');
  }

  /* Autogenerate on an EDIT form — allowed since 14 Sep 2026. */
  function generate() {
    const sample = samplePersonnel({ catalogs: NO_CATALOGS, identificationTypes: [] });

    setBranch(sample.militaryBranch);
    setStatus(sample.militaryServiceStatus);
    setRank(sample.militaryRank);
    setServiceNumber(sample.militaryServiceNumber);
    setUnit(sample.militaryUnit);
    setEntered(sample.militaryEntered);
    setSeparated(sample.militarySeparated);
  }

  function build() {
    return {
      militaryBranch: branch,
      militaryServiceStatus: status,
      militaryRank: rank.trim() || null,
      militaryServiceNumber: serviceNumber.trim() || null,
      militaryUnit: unit.trim() || null,
      militaryDateEnteredService: entered || null,
      militaryDateSeparated: separated || null,
    };
  }

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit military service"
      idPrefix="personnel-military"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
    >
      <Fields
        branch={branch}
        setBranch={setBranch}
        status={status}
        setStatus={setStatus}
        rank={rank}
        setRank={setRank}
        serviceNumber={serviceNumber}
        setServiceNumber={setServiceNumber}
        unit={unit}
        setUnit={setUnit}
        entered={entered}
        setEntered={setEntered}
        separated={separated}
        setSeparated={setSeparated}
      />
    </PersonnelSectionModal>
  );
}

function Fields(props: {
  branch: MilitaryBranch | null;
  setBranch: (next: MilitaryBranch | null) => void;
  status: MilitaryServiceStatus | null;
  setStatus: (next: MilitaryServiceStatus | null) => void;
  rank: string;
  setRank: (next: string) => void;
  serviceNumber: string;
  setServiceNumber: (next: string) => void;
  unit: string;
  setUnit: (next: string) => void;
  entered: string;
  setEntered: (next: string) => void;
  separated: string;
  setSeparated: (next: string) => void;
}) {
  const { saving, error } = useSectionForm();
  const today = todayCalendarDate();

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id="personnel-military-branch"
          label="Branch"
          required
          value={props.branch === null ? '' : String(props.branch)}
          onChange={(next) => props.setBranch(next ? (Number(next) as MilitaryBranch) : null)}
          options={Object.entries(MILITARY_BRANCH_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          sort="none"
          error={error?.fieldError('militaryBranch')}
          disabled={saving}
        />

        <Select
          id="personnel-military-service-status"
          label="Status"
          value={props.status === null ? '' : String(props.status)}
          onChange={(next) =>
            props.setStatus(next ? (Number(next) as MilitaryServiceStatus) : null)
          }
          options={Object.entries(MILITARY_SERVICE_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          sort="none"
          error={error?.fieldError('militaryServiceStatus')}
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="personnel-military-rank"
          label="Rank"
          value={props.rank}
          onChange={(e) => props.setRank(e.target.value)}
          error={error?.fieldError('militaryRank')}
          disabled={saving}
        />

        <TextField
          id="personnel-military-service-number"
          label="Serial / service number"
          value={props.serviceNumber}
          onChange={(e) => props.setServiceNumber(e.target.value)}
          error={error?.fieldError('militaryServiceNumber')}
          disabled={saving}
        />
      </div>

      <TextField
        id="personnel-military-unit"
        label="Unit / assignment"
        value={props.unit}
        onChange={(e) => props.setUnit(e.target.value)}
        error={error?.fieldError('militaryUnit')}
        disabled={saving}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id="personnel-military-date-entered-service"
          label="Entered service"
          value={props.entered}
          onChange={props.setEntered}
          max={today}
          error={error?.fieldError('militaryDateEnteredService')}
          disabled={saving}
        />

        <DateField
          id="personnel-military-date-separated"
          label="Separated"
          value={props.separated}
          onChange={props.setSeparated}
          // Separation cannot precede the date service began — the server enforces it, and
          // the picker says so before the reader submits.
          min={props.entered || undefined}
          max={today}
          error={error?.fieldError('militaryDateSeparated')}
          disabled={saving}
        />
      </div>
    </>
  );
}
